import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Badge, Card, Input, Table, Tbody, Td, Th, Thead, Tr } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { PeopleAPI, fullName, type Person } from '@/api/people'
import { ENRICHMENT_VARIANT, confidenceTier, scoreTier } from '@/lib/badges'

export function People() {
  const { diwaUrl } = useUI()
  const navigate = useNavigate()
  const [q, setQ] = useState('')

  const api = new PeopleAPI(diwaUrl)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['people', q],
    queryFn: () => api.list({ q: q || undefined, limit: 200 }),
  })

  const people = data?.people ?? []

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">People</div>
          {data && <div className="dw-page-subtitle">{data.total} in your graph</div>}
        </div>
        <div style={{ width: 240 }}>
          <Input
            type="search"
            placeholder="Search people…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <Card>
        {isLoading && <p className="dw-empty">Loading…</p>}
        {isError && <p className="dw-error">Couldn’t load people: {(error as Error).message}</p>}
        {!isLoading && !isError && people.length === 0 && (
          <p className="dw-empty">No people yet. Import contacts or capture a profile to populate your graph.</p>
        )}
        {people.length > 0 && (
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Title</Th>
                <Th>Company</Th>
                <Th>Score</Th>
                <Th>Confidence</Th>
                <Th>Enrichment</Th>
              </Tr>
            </Thead>
            <Tbody>
              {people.map((p: Person) => {
                const st = scoreTier(p.relationship_score)
                const ct = confidenceTier(p.enrichment_confidence)
                return (
                  <Tr key={p.id} className="dw-row-clickable" onClick={() => navigate(`/people/${p.id}`)}>
                    <Td>{fullName(p)}</Td>
                    <Td>{p.title ?? '—'}</Td>
                    <Td>{p.company_name ?? '—'}</Td>
                    <Td>
                      <Badge variant={st.variant}>{p.relationship_score.toFixed(0)} · {st.label}</Badge>
                    </Td>
                    <Td>
                      <Badge variant={ct.variant}>{ct.label}</Badge>
                    </Td>
                    <Td>
                      <Badge variant={ENRICHMENT_VARIANT[p.enrichment_status] ?? 'neutral'}>
                        {p.enrichment_status}
                      </Badge>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        )}
      </Card>
    </div>
  )
}
